import type { ApplicationContext } from '@hc-frontend/core-context';
import type { IErrorOperationResult } from '@hc-frontend/core-utils-business-operation';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import type { RawPlateAnswers } from '../../entities';
import { PartialSurveyAnswers } from '../../entities';
import type {
  ExtractSurveyAnswersRepository,
  ExtractSurveyAnswersResult,
} from '../../repositories';

export type ExtractSurveyAnswersUseCase = (
  rawPlateAnswers: RawPlateAnswers,
) => Promise<ExtractSurveyAnswersResult>;

type ExtractSurveyAnswersFactoryParameters = ExtractSurveyAnswersRepository &
  Pick<ApplicationContext, 'log'>;

const logOperation = (
  log: ApplicationContext['log'],
  result: IErrorOperationResult<string>,
) => {
  log({
    context: 'server',
    type: 'error',
    message: result.errors[0].code,
    data: {
      rootCause: result.errors[0].rootCause,
    },
  });
};

export function extractSurveyAnswersFactory({
  extractSurveyAnswers,
  log,
}: ExtractSurveyAnswersFactoryParameters): ExtractSurveyAnswersUseCase {
  return async (parameters) => {
    const extractSurveyAnswersResponse = await extractSurveyAnswers(parameters);

    if (!extractSurveyAnswersResponse.success) {
      logOperation(log, extractSurveyAnswersResponse);

      return extractSurveyAnswersResponse;
    }

    const parsedSurveyAnswers = PartialSurveyAnswers.safeParse(
      extractSurveyAnswersResponse.data,
    );

    if (!parsedSurveyAnswers.success) {
      const error = createErrorOperationResult({
        errors: [
          {
            code: 'failedToExtractSurveyAnswers',
            rootCause: parsedSurveyAnswers.error,
          },
        ],
      });

      logOperation(log, error);

      return error;
    }

    return createSuccessOperationResult(parsedSurveyAnswers.data);
  };
}
